import { useState, useEffect } from "react";
import { FolderPlus } from "lucide-react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch";
import { useToast } from "../../context/ToastContext";
import styles from "./AddEditFAQModal.module.css";

const DEFAULT_LANG = "en";

const emptyForm = {
  question: "",
  answer: "",
  shortAnswer: "",
  faqCategoryId: "",
  sortOrder: 0,
  status: "ACTIVE",
  isFeatured: false,
};

const AddEditFAQModal = ({
  onManageCategories,
  open,
  onClose,
  mode = "add",
  initialData,
  categories = [],
  onSubmit,
  submitting = false,
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setForm({
        question: initialData.question ?? "",
        answer: initialData.answer ?? "",
        shortAnswer: initialData.shortAnswer ?? "",
        faqCategoryId: initialData.faqCategoryId != null ? String(initialData.faqCategoryId) : "",
        sortOrder: initialData.sortOrder ?? 0,
        status: initialData.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
        isFeatured: initialData.isFeatured ?? false,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.question.trim()) {
      showToast("Question is required", "error");
      return;
    }
    if (!form.answer.trim()) {
      showToast("Answer is required", "error");
      return;
    }

    const payload = {
      faqCategoryId: form.faqCategoryId ? parseInt(form.faqCategoryId, 10) : null,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      status: form.status,
      isFeatured: form.isFeatured,
      descriptions: [
        {
          languageCode: DEFAULT_LANG,
          question: form.question.trim(),
          answer: form.answer.trim(),
          shortAnswer: form.shortAnswer.trim() || undefined,
        },
      ],
    };

    onSubmit(payload);
  };

  // True once any category has a parent. Drives the hint under the picker:
  // with nothing nested, the select is a flat list and says nothing about
  // subcategories existing at all.
  const hasSubcategories = (categories || []).some((c) => c.parentId);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Create FAQ" : "Edit FAQ"}
      width="720px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className={styles.primaryBtn}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : mode === "add" ? "Save FAQ" : "Save Changes"}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.fieldWide}>
          <label>Question</label>
          <input
            placeholder="Enter the question"
            value={form.question}
            onChange={(e) => handleChange("question", e.target.value)}
          />
        </div>

        <div className={styles.fieldWide}>
          <label>Answer</label>
          <textarea
            placeholder="Write a clear and helpful answer"
            value={form.answer}
            onChange={(e) => handleChange("answer", e.target.value)}
          />
        </div>

        <div className={styles.fieldWide}>
          <label>Short answer (optional)</label>
          <input
            placeholder="Brief summary for list view"
            value={form.shortAnswer}
            onChange={(e) => handleChange("shortAnswer", e.target.value)}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>Category</label>
            <select
              value={form.faqCategoryId}
              onChange={(e) => handleChange("faqCategoryId", e.target.value)}
            >
              <option value="">No category</option>
              {/* Each main category, with its subcategories grouped beneath it.
                  A question filed against a subcategory shows on its parent's
                  card on the site, filtered to that subcategory. */}
              {(categories || [])
                .filter((cat) => !cat.parentId)
                .map((parent) => {
                  const subs = (categories || []).filter(
                    (c) => Number(c.parentId) === Number(parent.id)
                  );
                  const label = parent.title || `Category ${parent.id}`;
                  if (!subs.length) {
                    return (
                      <option key={parent.id} value={parent.id}>
                        {label}
                      </option>
                    );
                  }
                  return (
                    <optgroup key={parent.id} label={label}>
                      {/* The parent stays selectable: a question can belong to
                          the category as a whole rather than to one of its
                          subcategories. */}
                      <option value={parent.id}>{label} (all)</option>
                      {subs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title || `Category ${c.id}`}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              {/* A subcategory orphaned by a deleted parent must stay pickable. */}
              {(categories || [])
                .filter(
                  (c) =>
                    c.parentId &&
                    !(categories || []).some((p) => Number(p.id) === Number(c.parentId))
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title || `Category ${c.id}`}
                  </option>
                ))}
            </select>
            {onManageCategories && (
              <button
                type="button"
                className={styles.manageLink}
                onClick={onManageCategories}
              >
                <FolderPlus size={13} />
                {hasSubcategories ? "Manage categories" : "Add a subcategory"}
              </button>
            )}
          </div>

          <div className={styles.field}>
            <label>Sort order</label>
            <input
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => handleChange("sortOrder", e.target.value)}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.statusBox}>
            <label>{form.status === "ACTIVE" ? "Visible" : "Hidden"}</label>
            <ToggleSwitch
              checked={form.status === "ACTIVE"}
              onChange={() =>
                handleChange("status", form.status === "ACTIVE" ? "INACTIVE" : "ACTIVE")
              }
            />
          </div>
          <div className={styles.statusBox}>
            <label>Featured</label>
            <ToggleSwitch
              checked={form.isFeatured}
              onChange={() => handleChange("isFeatured", !form.isFeatured)}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddEditFAQModal;
